import { TeacherPublicClassPostCommentUnreadCountQuery } from "@outschool/gql-frontend-generated";
import { useQuery } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import React from "react";

import { teacherPublicClassPostCommentUnreadCountQuery } from "../queries/TeacherMessagingQueries";
import { useHybridClassroom } from "./useHybridClassroom";
import { useTeacherUnifiedMessaging } from "./useTeacherUnifiedMessaging";

const UNREAD_COUNT_FRESHNESS_WINDOW_MS = 30 * 1000;

// Shared across every mounted badge so a focus event triggers one refetch, not
// one per badge.
let lastRefreshAt = Date.now();

function useUnreadPublicClassPostCommentThreadsCountQuery(
  loadFromNetwork: boolean,
) {
  const { currentUser, isLeader } = useSession();
  const isHybridClassroomEnabled = useHybridClassroom();
  const isTeacherUnifiedMessagingEnabled = useTeacherUnifiedMessaging();
  const skip =
    !currentUser ||
    !isLeader ||
    Boolean(currentUser?.sellerOrg?.currentUserIsOwner) ||
    !isHybridClassroomEnabled ||
    !isTeacherUnifiedMessagingEnabled;
  const { data, refetch } =
    useQuery<TeacherPublicClassPostCommentUnreadCountQuery>(
      teacherPublicClassPostCommentUnreadCountQuery,
      {
        fetchPolicy: loadFromNetwork ? "cache-and-network" : "cache-first",
        skip,
      },
    );

  React.useEffect(() => {
    if (
      skip ||
      typeof window === "undefined" ||
      typeof document === "undefined"
    ) {
      return;
    }

    const refreshIfVisible = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      const now = Date.now();
      if (now - lastRefreshAt < UNREAD_COUNT_FRESHNESS_WINDOW_MS) {
        return;
      }
      lastRefreshAt = now;
      refetch().catch(() => undefined);
    };

    window.addEventListener("focus", refreshIfVisible);
    document.addEventListener("visibilitychange", refreshIfVisible);
    return () => {
      window.removeEventListener("focus", refreshIfVisible);
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, [refetch, skip]);

  return data?.currentUser?.unreadPublicClassPostCommentThreadsCount ?? 0;
}

export function useLoadUnreadPublicClassPostCommentThreadsCount() {
  useUnreadPublicClassPostCommentThreadsCountQuery(true);
}

export function useUnreadPublicClassPostCommentThreadsCount(): number {
  return useUnreadPublicClassPostCommentThreadsCountQuery(false);
}
